import { useEffect, useRef } from 'react';
import { useRewardsStore } from '@/store/rewards.store';
import { useReadersStore } from '@/store/readers.store';
import { usePurchaseEgg } from '@/hooks/usePurchaseEgg';
import { usePaywall } from '@/hooks/usePaywall';
import { PaywalledSection } from '@/components/paywall/PaywalledSection';
import { PaywallLock } from '@/components/paywall/PaywallLock';
import { EggShopCard } from './EggShopCard';
import type { EggTierId } from '@/types/rewards';

export function EggShop() {
  const eggTiers = useRewardsStore((s) => s.eggTiers);
  const activeEggs = useRewardsStore((s) => s.activeEggs);
  const coinBalance = useReadersStore((s) => s.selectedCoinBalance()) ?? 0;
  const { purchase, purchasing } = usePurchaseEgg();
  const { isPaywalled, openPaywall } = usePaywall();
  const nestFull = activeEggs.length >= 2;

  // Scroll-into-view trigger: when a free-tier reader scrolls down to
  // the Egg Shop, fire the paywall once per page mount so they discover
  // the gated feature without needing to click through it. Repeated
  // entries (scrolling away and back) don't re-fire — that would feel
  // like a dunk-tank popup. The PaywalledSection wrapper below also
  // routes any click on the (now non-interactive) cards to the same
  // modal, so the user can re-open it on demand.
  const sectionRef = useRef<HTMLElement | null>(null);
  const firedRef = useRef(false);
  useEffect(() => {
    if (!isPaywalled || firedRef.current) return;
    const el = sectionRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !firedRef.current) {
            firedRef.current = true;
            openPaywall('egg-shop');
            obs.disconnect();
            break;
          }
        }
      },
      { threshold: 0.3 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [isPaywalled, openPaywall]);

  return (
    <section ref={sectionRef}>
      <div className="mb-1 flex items-center gap-2">
        <h2 className="text-2xl font-display font-bold text-stone-900">Egg Shop</h2>
        {isPaywalled ? <PaywallLock size="md" /> : null}
      </div>
      <p className="text-sm text-stone-500 mb-5">
        Choose an egg to hatch. Rarer eggs take longer but hold rarer creatures.
      </p>
      <PaywalledSection feature="egg-shop">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-4">
          {eggTiers.map((tier) => (
            <EggShopCard
              key={tier.id}
              tier={tier}
              coinBalance={coinBalance}
              nestFull={nestFull}
              onBuy={(id) => purchase(id as EggTierId)}
              purchasing={purchasing}
            />
          ))}
        </div>
      </PaywalledSection>
    </section>
  );
}
